import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MobileKeyboardService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly _isKeyboardOpen$ = new BehaviorSubject<boolean>(false);
  private readonly _keyboardHeight$ = new BehaviorSubject<number>(0);
  private readonly isBrowser: boolean;
  private initialViewportHeight = 0;
  private lastFocusedElement: HTMLElement | null = null;

  readonly isKeyboardOpen$: Observable<boolean> = this._isKeyboardOpen$.asObservable();
  readonly keyboardHeight$: Observable<number> = this._keyboardHeight$.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser) {
      this.initialViewportHeight = window.innerHeight;
      this.initializeListeners();
    }
  }

  private initializeListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      // Use Visual Viewport API if available (better for iOS Safari)
      if (window.visualViewport) {
        fromEvent(window.visualViewport, 'resize')
          .pipe(debounceTime(50), takeUntil(this.destroy$))
          .subscribe(() => this.handleViewportChange());

        fromEvent(window.visualViewport, 'scroll')
          .pipe(debounceTime(50), takeUntil(this.destroy$))
          .subscribe(() => this.handleViewportScroll());
      } else {
        // Fallback for browsers without Visual Viewport API
        fromEvent(window, 'resize')
          .pipe(debounceTime(100), takeUntil(this.destroy$))
          .subscribe(() => this.handleWindowResize());
      }

      // Listen for focus events on input elements
      fromEvent<FocusEvent>(this.document, 'focusin')
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => this.handleFocusIn(event));

      fromEvent<FocusEvent>(this.document, 'focusout')
        .pipe(debounceTime(100), takeUntil(this.destroy$))
        .subscribe(event => this.handleFocusOut(event));
    });
  }

  private handleViewportChange(): void {
    if (!window.visualViewport) return;

    const viewportHeight = window.visualViewport.height;
    const windowHeight = window.innerHeight;
    const heightDiff = windowHeight - viewportHeight;

    // Keyboard is considered open if the viewport is significantly smaller
    const isOpen = heightDiff > 100;

    this.ngZone.run(() => {
      this._isKeyboardOpen$.next(isOpen);
      this._keyboardHeight$.next(isOpen ? heightDiff : 0);
    });

    if (isOpen) {
      this.updateCSSVariables(heightDiff);
      this.scrollFocusedElementIntoView();
    } else {
      this.resetCSSVariables();
    }
  }

  private handleViewportScroll(): void {
    if (this._isKeyboardOpen$.value && this.lastFocusedElement) {
      // Ensure the focused element stays visible during scroll
      this.scrollFocusedElementIntoView();
    }
  }

  private handleWindowResize(): void {
    const currentHeight = window.innerHeight;
    const heightDiff = this.initialViewportHeight - currentHeight;

    // On Android, keyboard opening reduces window.innerHeight
    const isOpen = heightDiff > 100;

    this.ngZone.run(() => {
      this._isKeyboardOpen$.next(isOpen);
      this._keyboardHeight$.next(isOpen ? heightDiff : 0);
    });

    if (isOpen) {
      this.updateCSSVariables(heightDiff);
      this.scrollFocusedElementIntoView();
    } else {
      this.resetCSSVariables();
      this.initialViewportHeight = currentHeight;
    }
  }

  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;

    if (this.isInputElement(target)) {
      this.lastFocusedElement = target;

      // Add class to body for CSS targeting
      this.document.body.classList.add('keyboard-focus-active');

      // Delay to allow keyboard to open
      setTimeout(() => {
        this.scrollFocusedElementIntoView();
      }, 300);
    }
  }

  private handleFocusOut(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;

    // Only clear if not focusing another input
    if (!relatedTarget || !this.isInputElement(relatedTarget)) {
      this.lastFocusedElement = null;
      this.document.body.classList.remove('keyboard-focus-active');
    }
  }

  private isInputElement(element: HTMLElement): boolean {
    if (!element) return false;

    const tagName = element.tagName.toLowerCase();
    const isInput = tagName === 'input' || tagName === 'textarea';
    const isContentEditable = element.isContentEditable;
    const hasContentEditableAttr = element.getAttribute('contenteditable') === 'true';

    // Check if it's an input type that triggers keyboard
    if (tagName === 'input') {
      const inputType = (element as HTMLInputElement).type?.toLowerCase();
      const nonKeyboardTypes = [
        'checkbox',
        'radio',
        'file',
        'submit',
        'button',
        'reset',
        'image',
        'hidden',
        'range',
        'color',
      ];
      if (nonKeyboardTypes.includes(inputType)) {
        return false;
      }
    }

    return isInput || isContentEditable || hasContentEditableAttr;
  }

  private scrollFocusedElementIntoView(): void {
    if (!this.lastFocusedElement) return;

    const element = this.lastFocusedElement;

    // Find the scrollable container
    const scrollContainer = this.findScrollableParent(element);

    // Calculate positions
    const elementRect = element.getBoundingClientRect();
    const keyboardHeight = this._keyboardHeight$.value;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight - keyboardHeight;

    // Check if element is below the visible area
    const safeAreaBottom = 20; // Extra padding above keyboard
    const visibleBottom = viewportHeight - safeAreaBottom;

    if (elementRect.bottom > visibleBottom) {
      const scrollAmount = elementRect.bottom - visibleBottom + safeAreaBottom;

      if (scrollContainer) {
        // Scroll the container
        scrollContainer.scrollBy({
          top: scrollAmount,
          behavior: 'smooth',
        });
      } else {
        // Scroll the window
        window.scrollBy({
          top: scrollAmount,
          behavior: 'smooth',
        });
      }
    }

    // Also use scrollIntoView as a fallback with block: 'center' for better positioning
    requestAnimationFrame(() => {
      if (this.lastFocusedElement && this._isKeyboardOpen$.value) {
        this.lastFocusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    });
  }

  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;

    while (parent && parent !== this.document.body) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
      const hasScroll = parent.scrollHeight > parent.clientHeight;

      if (isScrollable && hasScroll) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  }

  private updateCSSVariables(keyboardHeight: number): void {
    const root = this.document.documentElement;
    root.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
    root.style.setProperty('--safe-area-keyboard', `${keyboardHeight}px`);
    this.document.body.classList.add('keyboard-open');
  }

  private resetCSSVariables(): void {
    const root = this.document.documentElement;
    root.style.setProperty('--keyboard-height', '0px');
    root.style.setProperty('--safe-area-keyboard', '0px');
    this.document.body.classList.remove('keyboard-open');
  }

  get isKeyboardOpen(): boolean {
    return this._isKeyboardOpen$.value;
  }

  get keyboardHeight(): number {
    return this._keyboardHeight$.value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resetCSSVariables();
    this.document.body.classList.remove('keyboard-open', 'keyboard-focus-active');
  }
}
